import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { ApiQueryResponse } from 'app/shared/lib/essette-api';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { I18N } from 'aurelia-i18n';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/api/api';
import { TransitionalManager } from 'app/shared/lib/transitional/manager';
import { localSort } from 'app/shared/lib/sorting';
import { openFileInWindow } from 'app/shared/lib/files';
import { fireEvent } from 'app/shared/lib/fire-events';
import { CorrespondenceService } from 'app/core/services/correspondence-service';
import { ICorrespondenceConfig } from 'app/core/models/essette-interfaces';
import { Modal } from 'materialize-css';
import { deepClone } from 'app/shared/lib/utilities';
import { validate } from 'app/shared/elements/ess-rule';
import { EventAggregator } from 'aurelia-event-aggregator';
import { LetterCopyForwardedEvent } from 'app/core/lib/events';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class CorrespondenceHistoryCopiesGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.toView })
    protected copies: Core.CorrespondenceLetterCopy[] = [];

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected letterId: string | null = null;
    @bindable({ defaultBindingMode: bindingMode.toView })
    protected memberId: string | null = null;
    @bindable({ defaultBindingMode: bindingMode.toView })
    protected targetId: string | null = null;

    @bindable.objectNull({ defaultBindingMode: bindingMode.toView })
    public config: ICorrespondenceConfig | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected allowRetry: boolean = true;

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected allowForward: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    protected canPreviewDownloads: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    protected showRecipientDescription: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    protected showDownloadFileOption: boolean = false;

    @bindable()
    protected templateDescription: string = '';

    protected editedLetterCopy: Core.CorrespondenceLetterCopy = {};
    protected fileName: string = '';

    protected validate = validate;
    protected isValid = false;
    private sorting: Sorting = new Sorting();
    private defaultSorting: Sorting = new Sorting();

    ////****************** Element References ***************************************////
    protected $forwardLetterCopyModal!: Modal;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly $element: Element,
        private readonly events: EventAggregator,
        private readonly service: CorrespondenceService,
        private readonly transitional: TransitionalManager,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
        this.defaultSorting.entries = [
            {
                reference: 'delivered.recipient.description',
                direction: SortDirection.Ascending
            }
        ];
    }

    ////****************** Events ***************************************************////
    protected bind(): void {
        if(!this.config) { throw new Error('Bindable property [config] was not set'); }
        this.service.configure(this.config);
        // if letterId is provided with no copies, then this component must load its own data
        if(this.letterId && !this.copies?.length) this.loadHistoryLetterCopies();
    }

    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting, defaultSorting } = this;
        const copies: Core.CorrespondenceLetterCopy[] = <Core.CorrespondenceLetterCopy[]>this.copies;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        localSort(copies, sorting.entries.length ? sorting : defaultSorting);
    }

    protected async handleLetterCopyForwarded(): Promise<void> {
        this.$forwardLetterCopyModal.close();
        this.events.publish(new LetterCopyForwardedEvent());
    }

    /****************** Functions **********************************************/
    protected openLetterCopyPreview(copy: Core.CorrespondenceLetterCopy): void {
        if(!copy || !copy.letter || !this.config?.previewTransitionalPageId) return;
        const w: Window | null = open('', 'Popup_PreviewLetter', `top=25,left=${(screen.width / 4)},width=1000,height=${(screen.height - 150)}`);
        const transitionalIds: string[] = [copy.letter.id!, copy.id!];
        this.transitional.openTransitional(w, this.config?.previewTransitionalPageId, transitionalIds);
    }

    protected openForwardLetterCopyModal(copy: Core.CorrespondenceLetterCopy): void {
        if(!copy || !copy.letter) return;
        this.editedLetterCopy = deepClone(copy);
        if(this.editedLetterCopy.letter?.template?.$display) this.fileName = this.editedLetterCopy.letter.template.$display;
        if(this.fileName.length > 30) this.fileName = `${this.fileName.substring(0, 30)}...`;
        this.$forwardLetterCopyModal.open();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async handleDownloadFile(event: MouseEvent, letterCopy: Core.CorrespondenceLetterCopy): Promise<void> {
        const childWindow = event.shiftKey ? window.open('', '_blank') : window.open();
        if(!childWindow || !letterCopy || !letterCopy.id) { return; }

        try {
            const blob = await this.service.downloadLetterCopy(letterCopy.id);
            openFileInWindow(childWindow, blob);
        }
        catch(error) {
            this.toaster.error().message(this.i18n.tr('ui:messages.errors.download', { what: 'core:correspondence.$' }));
            childWindow.close();
        }
    }

    @apiCall
    protected async retryLetterCopy(copy: Core.CorrespondenceLetterCopy): Promise<void> {
        let isSuccessful = false;
        try {
            this.state.beginSave();

            const response = await this.service.resendLetterCopy(copy);
            isSuccessful = response.isSuccessful;
            fireEvent(this.$element, 'lettercopy-change');
        }
        catch(error) {
            throw error;
        }
        finally {
            if(isSuccessful) this.toaster.info().message(this.i18n.tr('core:correspondenceLetter.notifications.queuedPrinting')).show();
            else this.toaster.error().message(this.i18n.tr('core:correspondenceLetter.notifications.failedRetry')).show();
            this.state.endSave();
        }
    }

    @apiCall
    private async loadHistoryLetterCopies(): Promise<void> {
        if(!this.letterId || !this.memberId || !this.targetId) return;

        const includes = [
            'copies',
            'copies.coverSheet.templateVersion.template',
            'copies.deliveryMethod',
            'copies.delivered.recipient',
            'copies.delivered.to.type',
            'copies.letter',
            'copies.letter.template',
            'copies.status'
        ];

        const fields = [
            'copies.coverSheet.templateVersion.template.description',
            'copies.delivered.recipient.description',
            'copies.deliveryMethod.code',
            'copies.deliveryMethod.description',
            'copies.status.code',
            'copies.status.description',
            'copies.letter.template.description'
        ];

        try {
            this.state.beginLoad();

            let response: ApiQueryResponse<Core.CorrespondenceLetter> | null = null;
            if(this.config?.rollupByMember && this.memberId) {
                response = await this.service.loadLetter(this.letterId, undefined, includes, fields);
            } else if(this.targetId) {
                response = await this.service.loadLetterByTarget(this.letterId, this.targetId, undefined, includes, fields);
            }

            this.copies = response?.results[0].copies ?? [];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
